import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/services/auth/AuthContext";
import { clearViewCache } from "@/services/cache/viewCache";
import useBoundStore from "@/services/store/BoundStore";
import { setLinkedAccountRefusalHandler } from "@/services/api/hook";
import {
  FLEX_SNAPSHOT_PREFIX,
  SELECTED_VIEW_PREFIX,
  VIEW_PREFIX
} from "@/entities/dataTables/helpers";

const ACCOUNT_SCOPED_SESSION_PREFIXES = [
  VIEW_PREFIX,
  SELECTED_VIEW_PREFIX,
  FLEX_SNAPSHOT_PREFIX
];

const clearAccountScopedSessionState = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < sessionStorage.length; index++) {
      const key = sessionStorage.key(index);
      if (
        key &&
        ACCOUNT_SCOPED_SESSION_PREFIXES.some((prefix) => key.startsWith(prefix))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage unavailable — nothing was persisted there.
  }
};

/**
 * Moves the webapp between the home account and its child accounts. Every
 * cache that holds account data is dropped and the user lands on home, so no
 * record from one account is ever rendered while acting in another.
 */
export const useSwitchAccount = () => {
  const {
    tenantId,
    homeTenantId,
    childAccountIds,
    isLinkedAccount,
    setActiveAccountId
  } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const switchTo = useCallback(
    (accountId: string | null) => {
      navigate("/", { replace: true });
      setActiveAccountId(accountId);
      queryClient.clear();
      clearViewCache();
      clearAccountScopedSessionState();
      useBoundStore.setState(useBoundStore.getInitialState(), true);
    },
    [navigate, setActiveAccountId, queryClient]
  );

  const switchToHome = useCallback(() => switchTo(null), [switchTo]);

  return {
    activeAccountId: tenantId,
    homeTenantId,
    childAccountIds: childAccountIds ?? [],
    isLinkedAccount,
    switchTo,
    switchToHome
  };
};

/**
 * A 401 for a child account means the user may no longer act in it. The saved
 * choice is dropped and the app returns to the home account with the same
 * clearing as a manual switch, instead of asking the user to log in again.
 * A 401 for the home account is left to the normal re-login flow, so this can
 * never bounce between accounts.
 */
export const useReturnHomeOnLinkedAccountRefusal = (): void => {
  const { activeAccountId, homeTenantId, switchToHome } = useSwitchAccount();
  const stateRef = useRef({ activeAccountId, homeTenantId, switchToHome });

  useEffect(() => {
    stateRef.current = { activeAccountId, homeTenantId, switchToHome };
  }, [activeAccountId, homeTenantId, switchToHome]);

  useEffect(() => {
    setLinkedAccountRefusalHandler((refusedTenantId) => {
      const state = stateRef.current;
      if (!state.homeTenantId || refusedTenantId === state.homeTenantId) {
        return false;
      }
      if (refusedTenantId === state.activeAccountId) {
        // Several requests can be refused in the same tick; switch once.
        state.activeAccountId = state.homeTenantId;
        state.switchToHome();
      }
      return true;
    });
    return () => setLinkedAccountRefusalHandler(null);
  }, []);
};
